// What a store row shows: the port of RecordRow / RecordCardCompact (GroundTeam/Views/Components/RecordRow.swift) as a
// plain model, so the rules (which subtitle, which chips, which counts, which color) are unit-testable without a DOM
// and StoreRow.tsx stays a thin renderer. Everything here is derived from the decoded record, its derived stats
// (visit counts and dates are never on the wire, WEB_PLAN.md §3.10) and lookups built once per data change.

import { PRIORITY_COLORS, PRIORITY_TITLES, RECORD_TYPE_TITLES, type Priority, type RecordType } from '@/contracts/enums';
import type { CustomFieldPayload, RecordPayload, StagePayload, UserPayload, VisitResultPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import { calendarDaysBetween } from '@/domain/calendar';
import { statsOf, type RecordStats } from '@/domain/derived';
import { daysSinceText, formatMoney, formatShortDateTime, visitCountText } from '@/domain/format';
import { stageFor } from '@/domain/pipelines';
import {
  DAYS_SINCE_VISIT_COLORS,
  PIN_GRAY,
  RECORD_TYPE_PIN_COLORS,
  cardFieldsLine,
  recordDisplayName,
  recordSubtitle,
  type RecordLookups,
} from '@/domain/records';
import { presenceOf, type Presence } from '@/domain/team';
import { fullName, lookup } from '@/domain/util';

// MARK: - Visit recency

export type VisitRecencyBucket = 'recent' | 'month' | 'old' | 'never';

export interface VisitRecency {
  /** Calendar days since the last visit (0 = today); undefined = never visited. */
  days: number | undefined;
  bucket: VisitRecencyBucket;
  /** The iOS days-since-visit color (map pins and legend): ≤ 7 green, ≤ 30 amber, older red, never gray. */
  color: string;
  /** `daysSinceText`: "Never visited", "Visited today", "1 day from last visit", "12 days from last visit". */
  text: string;
  /** Table form: "Never", "Today", "1 day", "12 days". */
  shortText: string;
}

/**
 * How long since the last visit, with the iOS wording and the iOS days-since-visit color scale (the same buckets as
 * `pinColorHex(.daysSinceVisit)` and the legend's "0–7 days / 8–30 days / 31+ days / Never visited").
 */
export function visitRecency(lastVisitAt: Date | null | undefined, now: Date = new Date()): VisitRecency {
  if (!lastVisitAt) {
    return { days: undefined, bucket: 'never', color: DAYS_SINCE_VISIT_COLORS.never, text: daysSinceText(null, now), shortText: 'Never' };
  }
  // A visit dated in the future (a phone with a wrong clock) counts as today, as daysSinceText does.
  const days = Math.max(0, calendarDaysBetween(lastVisitAt, now));
  const bucket: VisitRecencyBucket = days <= 7 ? 'recent' : days <= 30 ? 'month' : 'old';
  return {
    days,
    bucket,
    color: DAYS_SINCE_VISIT_COLORS[bucket],
    text: daysSinceText(lastVisitAt, now),
    shortText: days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`,
  };
}

// MARK: - Lookups

/**
 * Everything a StoreRow needs besides the record: the domain `RecordLookups` (derived stats, users, pipelines, visit
 * results, now) plus the company currency, the "show on card" custom fields and live presence. Build once per page
 * with `useStoreRowLookups()` and pass the same object to every row (rows are memoized on it).
 */
export interface StoreRowLookups extends RecordLookups {
  currencyCode: string;
  customFields: readonly CustomFieldPayload[];
  /** Presence by lower-case user id (from the reps' last positions); missing = no dot. */
  presence: ReadonlyMap<string, Presence>;
  locale?: string;
}

/** Presence of every rep with a known position (`useRepPositions()`), as of `now`. */
export function presenceMap(positions: ReadonlyMap<string, { at: Date }>, now: Date): Map<string, Presence> {
  const out = new Map<string, Presence>();
  for (const [key, p] of positions) out.set(key, presenceOf(p.at, now));
  return out;
}

export function storeRowLookups(
  base: RecordLookups,
  extra: {
    currencyCode: string;
    customFields?: readonly CustomFieldPayload[];
    presence?: ReadonlyMap<string, Presence>;
    locale?: string;
  },
): StoreRowLookups {
  const lk: StoreRowLookups = {
    ...base,
    currencyCode: extra.currencyCode,
    customFields: extra.customFields ?? [],
    presence: extra.presence ?? new Map(),
  };
  if (extra.locale) lk.locale = extra.locale;
  return lk;
}

// MARK: - Row model

export interface StoreRowModel {
  /** Upper-case record id. */
  id: string;
  type: RecordType;
  /** "Lead" / "Account" / "Contact". */
  typeTitle: string;
  /** `RecordType.tint`: lead orange, account navy, contact purple. */
  typeColor: string;
  /** `Record.displayName`: name, else company, else "Untitled". */
  name: string;
  /** `Record.subtitle`: "Job title · Company" for contacts, the one-line address otherwise. */
  subtitle: string;
  /** "phone · email" (either may be missing). */
  contactLine: string;
  /** Custom fields flagged "Show on card": "Roof Type: Metal · Contract value: $12,000". */
  cardFields: string;
  doNotContact: boolean;
  archived: boolean;
  /** Written by a newer app: show read-only, never build an edit from it. */
  readOnly: boolean;
  priority: Priority;
  priorityColor: string;
  /** "A · Hot". */
  priorityTitle: string;
  stage: StagePayload | undefined;
  /** Result of the newest completed visit (RecordCardCompact's filled chip). */
  lastVisitResult: VisitResultPayload | undefined;
  visit: VisitRecency;
  visitCount: number;
  /** "No visits" / "1 visit" / "3 visits". */
  visitCountText: string;
  verifiedCount: number;
  /** The deal value in the company currency; undefined when 0 (iOS hides it). */
  valueText: string | undefined;
  /** "Last: Sep 22, 2:43 PM" source: the newest completed activity of any type. */
  lastActivityAt: Date | undefined;
  lastActivityText: string | undefined;
  nextScheduledAt: Date | undefined;
  owner: UserPayload | undefined;
  ownerName: string | undefined;
  ownerPresence: Presence | undefined;
  /** One-sentence accessible name for the row link or button. */
  ariaLabel: string;
}

/** The record's value as iOS shows it on a card: hidden at 0 (and for NaN), else `CompanySettings.money`. */
export function storeValueText(value: number, currencyCode: string, locale?: string): string | undefined {
  return Number.isFinite(value) && value > 0 ? formatMoney(value, currencyCode, locale) : undefined;
}

/** "phone · email", skipping empty parts (RecordRowIdentity.contactLine). */
export function contactLine(record: Pick<RecordPayload, 'phone' | 'email'>): string {
  return [record.phone, record.email].filter((s) => s !== '').join(' · ');
}

/**
 * Builds the row model. `stats` defaults to the record's entry in `lookups.stats` (pass it when you already have it,
 * e.g. from a sorted list). `item.value` is used even for undecodable items: it is the best-effort decode, shown
 * read-only.
 */
export function storeRowModel(item: DataItem<RecordPayload>, lookups: StoreRowLookups, stats?: RecordStats): StoreRowModel {
  const r = item.value;
  const st = stats ?? statsOf(lookups.stats, r.id);
  const now = lookups.now;
  const stage = stageFor(r, lookups.pipelines);
  const owner = lookup(lookups.users, r.ownerId);
  const ownerName = owner ? fullName(owner) || 'Unnamed' : undefined;
  const ownerPresence = owner ? lookup(lookups.presence, owner.id) : undefined;
  const visit = visitRecency(st.lastVisitAt, now);
  const typeTitle = RECORD_TYPE_TITLES[r.type] ?? 'Store';
  const priorityTitle = PRIORITY_TITLES[r.priority] ?? r.priority;
  const valueText = storeValueText(r.value, lookups.currencyCode, lookups.locale);
  const name = recordDisplayName(r);

  const spoken = [
    name,
    typeTitle,
    `Priority ${priorityTitle}`,
    stage ? `Stage ${stage.name}` : null,
    visit.text,
    valueText ?? null,
    ownerName ? `Owner ${ownerName}` : 'Unassigned',
    r.doNotContact ? 'Do not contact' : null,
    r.isArchived ? 'Archived' : null,
    item.undecodable ? 'Created by a newer app, read-only' : null,
  ].filter((s): s is string => s !== null);

  return {
    id: item.id,
    type: r.type,
    typeTitle,
    typeColor: RECORD_TYPE_PIN_COLORS[r.type] ?? PIN_GRAY,
    name,
    subtitle: recordSubtitle(r),
    contactLine: contactLine(r),
    cardFields: lookups.customFields.length > 0 ? cardFieldsLine(r, lookups.customFields, lookups.currencyCode, lookups.locale) : '',
    doNotContact: r.doNotContact,
    archived: r.isArchived,
    readOnly: item.undecodable !== undefined,
    priority: r.priority,
    priorityColor: PRIORITY_COLORS[r.priority] ?? PIN_GRAY,
    priorityTitle,
    stage,
    lastVisitResult: lookup(lookups.visitResults, st.lastVisitResultId),
    visit,
    visitCount: st.visitCount,
    visitCountText: visitCountText(st.visitCount),
    verifiedCount: st.verifiedVisitCount,
    valueText,
    lastActivityAt: st.lastActivityAt,
    lastActivityText: st.lastActivityAt ? `Last: ${formatShortDateTime(st.lastActivityAt, now, lookups.locale)}` : undefined,
    nextScheduledAt: st.nextScheduledAt,
    owner,
    ownerName,
    ownerPresence,
    ariaLabel: spoken.join(', '),
  };
}
